import React from 'react'

/**
 * Renders the small Markdown subset Gemini actually emits: bold, italics,
 * inline code, links, bullet and numbered lists, headings and paragraphs.
 *
 * Deliberately builds React elements instead of using dangerouslySetInnerHTML.
 * Model output is untrusted — anything it produces could contain markup, and
 * constructing elements makes HTML injection structurally impossible rather
 * than relying on a sanitizer being correct.
 */

// Order matters: ** must be tried before * so bold isn't parsed as two italics.
// The link URL permits one level of nested parentheses, so URLs containing
// them — and malformed ones like `javascript:alert(1)` — are consumed whole
// instead of leaving a stray `)` in the rendered text.
const INLINE_TOKEN =
  /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\[[^\]]+\]\((?:[^()]|\([^()]*\))*\)|\*[^*\n]+\*)/g

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = []
  const regex = new RegExp(INLINE_TOKEN)
  let lastIndex = 0
  let match: RegExpExecArray | null
  let n = 0

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index))

    const token = match[0]
    const key = `${keyPrefix}-i${n++}`

    if (token.startsWith('**') || token.startsWith('__')) {
      nodes.push(<strong key={key} className="font-semibold">{token.slice(2, -2)}</strong>)
    } else if (token.startsWith('`')) {
      nodes.push(
        <code key={key} className="px-1 py-0.5 rounded bg-black/10 font-mono text-[0.9em]">
          {token.slice(1, -1)}
        </code>
      )
    } else if (token.startsWith('[')) {
      // Must mirror the URL pattern in INLINE_TOKEN, or a token containing
      // nested parens fails to match here and gets printed as raw Markdown.
      const link = /^\[([^\]]+)\]\(((?:[^()]|\([^()]*\))*)\)$/.exec(token)
      const label = link?.[1] ?? token
      const href = link?.[2] ?? ''
      // Only http(s). Blocks javascript: and data: URLs, which an LLM can be
      // talked into emitting.
      if (/^https?:\/\//i.test(href)) {
        nodes.push(
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="underline break-all"
          >
            {label}
          </a>
        )
      } else {
        nodes.push(label)
      }
    } else {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>)
    }

    lastIndex = match.index + token.length
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
  return nodes
}

const BULLET = /^\s*[-*+]\s+/
const NUMBERED = /^\s*\d+[.)]\s+/
const HEADING = /^\s*(#{1,6})\s+(.*)$/

const FormattedMessage: React.FC<{ content: string; className?: string }> = ({
  content,
  className = '',
}) => {
  const lines = content.split('\n')
  const blocks: React.ReactNode[] = []
  let i = 0
  let key = 0

  while (i < lines.length) {
    const line = lines[i]

    if (!line.trim()) {
      i++
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      blocks.push(
        <p key={key++} className="font-semibold mt-2 first:mt-0">
          {renderInline(heading[2], `h${key}`)}
        </p>
      )
      i++
      continue
    }

    if (BULLET.test(line)) {
      const items: string[] = []
      while (i < lines.length && BULLET.test(lines[i])) {
        items.push(lines[i].replace(BULLET, ''))
        i++
      }
      blocks.push(
        <ul key={key++} className="list-disc pl-5 space-y-1 my-2 first:mt-0 last:mb-0">
          {items.map((item, n) => (
            <li key={n}>{renderInline(item, `u${key}-${n}`)}</li>
          ))}
        </ul>
      )
      continue
    }

    if (NUMBERED.test(line)) {
      const items: string[] = []
      while (i < lines.length && NUMBERED.test(lines[i])) {
        items.push(lines[i].replace(NUMBERED, ''))
        i++
      }
      blocks.push(
        <ol key={key++} className="list-decimal pl-5 space-y-1 my-2 first:mt-0 last:mb-0">
          {items.map((item, n) => (
            <li key={n}>{renderInline(item, `o${key}-${n}`)}</li>
          ))}
        </ol>
      )
      continue
    }

    // Paragraph: consume until a blank line or the start of another block.
    const paragraph: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !BULLET.test(lines[i]) &&
      !NUMBERED.test(lines[i]) &&
      !HEADING.test(lines[i])
    ) {
      paragraph.push(lines[i].trim())
      i++
    }
    blocks.push(
      <p key={key++} className="my-2 first:mt-0 last:mb-0">
        {renderInline(paragraph.join(' '), `p${key}`)}
      </p>
    )
  }

  return <div className={className}>{blocks}</div>
}

export default FormattedMessage
